import { withCookies } from "react-cookie";
import React, { useCallback, useEffect, useState } from "react";
import {
    AutoComplete,
    Button,
    Checkbox,
    Form,
    Input,
    message,
    Popconfirm,
    Select
} from "antd";
import { apiGet, apiPost, apiPut } from "helpers/api";
import { useHistory } from "react-router-dom";
import { removeDifficultiesFromTopicsTree } from "helpers/starterHelpers";
import { ListForm } from "../ListForm";
import MockPaperQuestionForm from "./MockPaperQuestionForm";

function AddMockPaperForm({ cookies, editingId = null }) {
    const [csrfToken] = useState(cookies.get("XSRF-TOKEN"));
    const history = useHistory();
    const [form] = Form.useForm();
    const isEditing = !(!editingId || form.getFieldValue("saveAsNew"));
    const [, toggleReRender] = useState(true);
    const [subTopics, setSubTopics] = useState([]);

    const loadSubTopics = useCallback(() => {
        setSubTopics([]);
        apiGet(
            "/api/all-topics-tree",
            (body) => {
                setSubTopics(removeDifficultiesFromTopicsTree(body, true));
            },
            () => {
                const error = {
                    value: "error",
                    label: "Error: Unable to retrieve topics",
                    disabled: true
                };
                setSubTopics([error]);
            }
        );
    }, []);

    // Load question topics
    useEffect(() => {
        loadSubTopics();
    }, [loadSubTopics]);

    const loadDataForEditing = useCallback(
        (id) => {
            apiGet("/api/mock-paper?id=" + encodeURIComponent(id), (body) => {
                const copy = {
                    examBoard: body.examBoard,
                    isHigher: body.higher,
                    groupName: body.group,
                    paperName: body.name,
                    shortCode: body.shortCode,
                    paperLink: body.paperDownloadLink,
                    markSchemeLink: body.markSchemeDownloadLink,
                    solutionsLink: body.workedSolutionsDownloadLink,
                    qlaLink: body.qlaDownloadLink,

                    questions: body.questions.map(
                        ({ hintImageUrl, parts: qs }) => ({
                            hintImage: hintImageUrl,
                            children: qs.map((q) => {
                                return {
                                    difficulty: q.difficulty,
                                    questionUrls: q.questionImageUrls,
                                    markSchemeUrls: q.markSchemeImageUrls,
                                    solutionUrls: q.workedSolutionImageUrls,
                                    marks: q.marks,
                                    subTopics: q.subTopics.map(
                                        ({ value: st }) => {
                                            const topicArea = subTopics.find(
                                                (ta) =>
                                                    (ta?.children || [])
                                                        .flatMap(
                                                            (t) =>
                                                                t?.children ||
                                                                []
                                                        )
                                                        .map((st) => st.value)
                                                        .includes(st)
                                            );
                                            const topic = subTopics
                                                .flatMap(
                                                    (ta) => ta?.children || []
                                                )
                                                .find((t) =>
                                                    (t?.children || [])
                                                        .map((st) => st.value)
                                                        .includes(st)
                                                );
                                            return [
                                                topicArea?.value,
                                                topic?.value,
                                                st
                                            ];
                                        }
                                    )
                                };
                            })
                        })
                    )
                };
                form && form.setFieldsValue(copy);
                toggleReRender((p) => !p);
            });
        },
        [form, subTopics]
    );

    useEffect(() => {
        if (!editingId) {
            return;
        }
        loadDataForEditing(editingId);
    }, [editingId, loadDataForEditing]);

    const onSubmit = useCallback(
        (formData) => {
            if (!formData?.questions) {
                formData.questions = [];
            }
            formData.questions = formData.questions.map(
                ({ hintImage, children: qs }) => ({
                    hint: hintImage,
                    parts: qs.map((q) => ({
                        ...q,
                        subTopics: (q?.subTopics ?? []).map((st) => st?.[2])
                    }))
                })
            );
            if (!editingId || formData?.saveAsNew) {
                apiPost(
                    "/api/add-mock-paper",
                    JSON.stringify(formData),
                    csrfToken,
                    async (res) => {
                        const body = await res.json();
                        message.success("Success");
                        history.replace(
                            "/admin/edit-mock-paper?id=" +
                                encodeURIComponent(body.id)
                        );
                    }
                );
            } else {
                apiPut(
                    "/api/update-mock-paper?id=" +
                        encodeURIComponent(editingId),
                    JSON.stringify(formData),
                    csrfToken
                );
            }
        },
        [csrfToken, editingId, history]
    );

    const examBoards = [
        "Edexcel",
        "AQA",
        "FoundationBooster",
        "OCR",
        "WJEC",
        "MathsPlannerMock",
        "Other"
    ];

    const paperGroups = ["Set A", "Set B", "Set C", "Set D", "FB"];

    const paperNames = ["Paper 1", "Paper 2", "Paper 3", "Booster Sheet 1"];

    const generateShortCode = (fieldsData) => {
        const board = fieldsData.find(({ name }) =>
            name.includes("examBoard")
        )?.value;
        const higher = fieldsData.find(({ name }) => name.includes("isHigher"))
            ?.value
            ? "H"
            : "F";
        let set =
            fieldsData
                .find(({ name }) => name.includes("groupName"))
                ?.value?.split(" ")
                ?.slice(-1)?.[0] ?? "";
        const name =
            fieldsData
                .find(({ name }) => name.includes("paperName"))
                ?.value?.split(" ")
                ?.slice(-1)?.[0] ?? "";

        let shortCode;
        if (board === "FoundationBooster") {
            shortCode = "FB-" + name;
        } else {
            if (set && (higher || name)) {
                set = "-" + set;
            }
            shortCode = name + higher + set;
        }
        form.setFieldsValue({ shortCode });
    };

    return (
        <>
            <h1>{"Mock Paper " + (isEditing ? "Editor" : "Creator")}</h1>
            <div className={"formContainer mock-paper"}>
                <Form
                    form={form}
                    onFieldsChange={(changedFields, allFields) => {
                        if (
                            !changedFields.some((field) =>
                                field.name.includes("shortCode")
                            )
                        ) {
                            generateShortCode(allFields);
                        }
                        toggleReRender((p) => !p);
                    }}
                    onFinish={onSubmit}
                    onFinishFailed={({ errorFields }) =>
                        message.error(
                            <>
                                <p>Failed to submit - please check data:</p>
                                {[
                                    ...new Set(
                                        errorFields
                                            .flatMap((obj) => obj.errors)
                                            .map((err) => err.toString())
                                    )
                                ].map((errorMessage) => (
                                    <p>{errorMessage}</p>
                                ))}
                            </>
                        )
                    }
                    requiredMark={false}
                >
                    <Form.Item
                        name={"examBoard"}
                        label={"Exam Board"}
                        rules={[{ required: true }]}
                        className={"topLabel"}
                    >
                        <Select
                            options={examBoards.map((opt) => ({
                                label: opt,
                                value: opt
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"isHigher"}
                        label={"Higher?"}
                        className={"topLabel"}
                        valuePropName="checked"
                    >
                        <Checkbox />
                    </Form.Item>
                    <Form.Item
                        name={"groupName"}
                        label={"Paper Set/Group"}
                        rules={[{ required: true }]}
                        className={"topLabel"}
                        extra={
                            <span className={"ant-form-item-explain"}>
                                This is used for grouping and filtering papers
                                on the frontend so please check spelling etc.
                            </span>
                        }
                    >
                        <AutoComplete
                            filterOption={(inputValue, option) =>
                                option.value
                                    .toUpperCase()
                                    .indexOf(inputValue.toUpperCase()) !== -1
                            }
                            placeholder="Select a group or enter a custom group name"
                        >
                            {paperGroups.map((opt) => (
                                <AutoComplete.Option key={opt} value={opt}>
                                    {opt}
                                </AutoComplete.Option>
                            ))}
                        </AutoComplete>
                    </Form.Item>
                    <Form.Item
                        name={"paperName"}
                        label={"Paper Name"}
                        rules={[{ required: true }]}
                        className={"topLabel"}
                    >
                        <AutoComplete
                            filterOption={(inputValue, option) =>
                                option.value
                                    .toUpperCase()
                                    .indexOf(inputValue.toUpperCase()) !== -1
                            }
                            placeholder="Select a name or enter a custom one"
                        >
                            {paperNames.map((opt) => (
                                <AutoComplete.Option key={opt} value={opt}>
                                    {opt}
                                </AutoComplete.Option>
                            ))}
                        </AutoComplete>
                    </Form.Item>
                    <Form.Item
                        name={"shortCode"}
                        label={"Code"}
                        rules={[{ required: true }]}
                        className={"topLabel"}
                        extra={
                            <span className={"ant-form-item-explain"}>
                                This will be auto-generated from the other
                                information you enter, or can be manually edited
                                afterwards
                            </span>
                        }
                    >
                        <Input />
                    </Form.Item>
                    <h2>Download Links</h2>
                    <Form.Item
                        name={"paperLink"}
                        label={"Paper"}
                        className={"topLabel"}
                    >
                        <Input
                            prefix={"s3://mathsplanner-resources/"}
                            placeholder={"MockPapers/edexcel/1-HA.pdf"}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"markSchemeLink"}
                        label={"M'k Scheme"}
                        className={"topLabel"}
                    >
                        <Input
                            prefix={"s3://mathsplanner-resources/"}
                            placeholder={"MockPapers/edexcel/1-HA_MS.pdf"}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"solutionsLink"}
                        label={"W. Solutions"}
                        className={"topLabel"}
                    >
                        <Input
                            prefix={"s3://mathsplanner-resources/"}
                            placeholder={"MockPapers/edexcel/1-HA_WS.pdf"}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"qlaLink"}
                        label={"QLA"}
                        className={"topLabel"}
                    >
                        <Input
                            prefix={"s3://mathsplanner-resources/"}
                            placeholder={"MockPapers/edexcel/1-HA_QLA.xlsx"}
                        />
                    </Form.Item>
                    <ListForm
                        name={"questions"}
                        label={"Question Mappings"}
                        labelFunction={() => undefined}
                        control={(field = {}, controlProps) => {
                            const questionHasParts =
                                form.getFieldValue("questions")?.[field?.name]
                                    ?.children?.length > 1;
                            const { name, ...fieldProps } = field;
                            return (
                                <div
                                    className={
                                        "mockPaperQuestionFormContainer mockPaperQuestionWrapper"
                                    }
                                >
                                    <ListForm
                                        {...field}
                                        label={`Q${field?.name + 1}`}
                                        topLevelControls={
                                            <Form.Item
                                                {...fieldProps}
                                                name={[name, "hintImage"]}
                                                label={"Hint Image"}
                                                labelCol={{ offset: 2 }}
                                                className={"listFormItem"}
                                            >
                                                <Input
                                                    placeholder="E.g. triangles/sine-rule-1.svg"
                                                    prefix={
                                                        process.env
                                                            .REACT_APP_IMAGE_DIRECTORY
                                                    }
                                                />
                                            </Form.Item>
                                        }
                                        labelFunction={(index) =>
                                            `${field?.name + 1}${
                                                questionHasParts
                                                    ? String.fromCharCode(
                                                          "a".charCodeAt(0) +
                                                              index
                                                      )
                                                    : ""
                                            }`
                                        }
                                        control={(
                                            subField = {},
                                            controlProps
                                        ) => (
                                            <MockPaperQuestionForm
                                                field={subField}
                                                subTopics={
                                                    controlProps?.subTopics ??
                                                    []
                                                }
                                                formFieldName={[
                                                    "questions",
                                                    field?.name,
                                                    "children",
                                                    subField?.name
                                                ]}
                                                form={controlProps?.form}
                                            />
                                        )}
                                        usesLocalAdd
                                        {...controlProps}
                                    />
                                </div>
                            );
                        }}
                        usesLocalAdd
                        subTopics={subTopics}
                        form={form}
                    />
                    <div className={"submitButtonContainer"}>
                        <Form.Item>
                            {!isEditing ? (
                                <Button
                                    key="submit"
                                    type="primary"
                                    htmlType="submit"
                                    className={"formSubmit"}
                                >
                                    Save
                                </Button>
                            ) : (
                                <Popconfirm
                                    title={
                                        <>
                                            Are you sure you want to{" "}
                                            <b>overwrite</b> the current save
                                            you are editing?
                                        </>
                                    }
                                    onConfirm={form.submit}
                                    onCancel={() => {}}
                                    okText="Overwrite"
                                    cancelText="Cancel"
                                >
                                    <Button
                                        key="submit"
                                        type="primary"
                                        htmlType="submit"
                                        className={"formSubmit"}
                                    >
                                        Update
                                    </Button>
                                </Popconfirm>
                            )}
                        </Form.Item>
                        {editingId && (
                            <Form.Item
                                name={"saveAsNew"}
                                label={"Save as new paper?"}
                                valuePropName="checked"
                            >
                                <Checkbox />
                            </Form.Item>
                        )}
                    </div>
                </Form>
            </div>
        </>
    );
}

export default withCookies(AddMockPaperForm);
